import { MouseEvent, useMemo } from 'react';
import MenuParams from 'shared/ui/menu-params';
import { IconButton, TableCell, TableRow, Box, Typography, Chip, Stack, Card, Tooltip } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {  INVOICE_STATUS_DEBT, INVOICE_STATUS_PAID, INVOICE_STATUS_PARTIAL_PAID, INVOICE_STATUSES, InvoiceInfoDto, InvoiceStatus, IPagingData } from '@sr/dto';
import { GridLayout, HeadCellsCollection } from 'shared/ui/grid-layout';
import { RowMenu, useMenu } from 'shared/ui/row-menu';
import { formatDate } from 'utils/date-format-helpers';
import { LinkToInvoice } from 'entities/client-invoices-list/ui/link-to-invoice';
import PriceSpan from 'shared/ui/price-span';
import { InvoiceStatusSpan } from '../status-span/status-span';
import { LinkToCompany } from 'shared/ui/links/link-to-company';
import { LinkToLegalEntity } from 'shared/ui/links/link-to-legal-entity';
import ManagerSpan from 'shared/ui/manager-span';
import { IRemotePagedCollection } from 'utils/remote-paged-collection.hook';
import { WccButton } from 'entities/wcc/wcc-button';
import { InvoiceCloseForm } from '../../../features/invoice/invoice-close/invoice-close.form';
import { calculateInvoiceReportTotals, calculateNetProfit } from '../../invoice-report/lib';
import { statusCounter } from '../../../utils/status-counter-helper';
import { useLastClosedInvoice } from '../invoice.hook';
import { formatPersonPruning } from 'utils/person-format-helpers';
import { InvoiceFilter, InvoiceFilterProps } from 'features/invoice/invoice-filter/invoice-filter';

type Props = {
  collection: IRemotePagedCollection<InvoiceInfoDto>;
  paging: IPagingData;
  onChangePaging?: (paging: IPagingData) => void;
  onCancel?: (invoice: InvoiceInfoDto) => void;
  withSalaryEntries?: boolean;
  readOnly?: boolean;
  withCounts?: boolean;
  withFilter?: boolean;
  invoiceFilterProps?: Omit<InvoiceFilterProps, 'withClosingPeriodFilter'>;
};

const commonColumns: HeadCellsCollection = [
  'Дата создания',
  'Номер',
  'Клиент',
  'Наше юрлицо',
  'Клиентское юрлицо',
  'Статус',
  'Менеджер'];

const withSalaryEntriesColumns: HeadCellsCollection = [
  ...commonColumns,
  'Последний закрытый счёт',
  { label: 'Сумма', align: 'right' },
  { label: 'Чистая прибыль', align: 'right' },
  'Кол-во заявок',
  'Период закрытия',
  'Ставка',
  ''
];

const defaultColumns: HeadCellsCollection = [
  ...commonColumns,
  { label: 'Сумма', align: 'right' },
  { label: 'Прибыль по заявкам', align: 'right' },
  { label: 'Чистая прибыль', align: 'right' },
  'Акт',
  ''
];

export const InvoicesList = ({ collection, onCancel, paging, onChangePaging, withSalaryEntries = false, readOnly = false, withCounts = false, withFilter = false, invoiceFilterProps }: Props) => {
  const { openMenu, menuData } = useMenu<InvoiceInfoDto>({ cancelHandler: onCancel });
  const columns: HeadCellsCollection = useMemo(() => {
    return withSalaryEntries ? withSalaryEntriesColumns : defaultColumns;
  }, [withSalaryEntries]);
  const statuses = statusCounter([INVOICE_STATUS_DEBT, INVOICE_STATUS_PAID, INVOICE_STATUS_PARTIAL_PAID], 'status', collection.collection.meta as Array<Record<string, number>>);
  const metaPriceTotal = collection.collection.meta?.find((x) => x['discountPriceTotal'] !== undefined)?.['discountPriceTotal'];
  const metaProfitByPurchasesTotal = collection.collection.meta?.find((x) => x['profitByPurchasesTotal'] !== undefined)?.['profitByPurchasesTotal'];
  const metaNetProfitTotal = collection.collection.meta?.find((x) => x['netProfitTotal'] !== undefined)?.['netProfitTotal'];
  const metaSalesPercentage = collection.collection.meta?.find((x) => x['salesPercentage'] !== undefined)?.['salesPercentage'];
  
  return (
    <>
      {withFilter && <InvoiceFilter withClosingPeriodFilter={true} {...invoiceFilterProps} />}
      {withCounts && collection.collection.meta && (
        <Box display="flex" gap={2} my={2} component="div" alignItems="baseline">
          Отображаются данные со статусами
          {statuses.map((item) => {
            return (
              <Chip
                key={item.status}
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" component="span">
                      {`${INVOICE_STATUSES[item.status as InvoiceStatus]} - `}
                    </Typography>
                    <Typography variant="body2" component="span" sx={{ fontWeight: 'bold' }}>
                      {item.count}
                    </Typography>
                  </Box>
                }
              />
            );
          })}
        </Box>
      )}
      <GridLayout
        columns={columns}
        items={collection.collection.items}
        isLoading={collection.isLoading}
        error={collection.error}
        noItemsText="Список счетов пуст"
        pagingData={paging}
        onChangePaging={onChangePaging}
        totalItemsCount={collection.collection.totalItemsCount}
        itemRender={(i) => <InvoiceListRow readOnly={readOnly} key={i.id} invoice={i} onMenuClick={openMenu} withSalaryEntries={withSalaryEntries} />}
        renderCustomHeader={() => (
          <InvoiceListHeader
            rows={collection.collection.items}
            metaNetProfitTotal={metaNetProfitTotal}
            metaPriceTotal={metaPriceTotal}
            metaProfitTotal={metaProfitByPurchasesTotal}
            metaSalesPercentage={metaSalesPercentage}
          />
        )}
        footer={(items) => <InvoiceListFooter rows={items} colSpan={withSalaryEntries ? 8 : 7} withSalaryEntries={withSalaryEntries} metaPriceTotal={metaPriceTotal} metaSalesByPurchasesTotal={metaProfitByPurchasesTotal} metaNetProfitTotal={metaNetProfitTotal}/>}
      />

      <RowMenu menuData={menuData} />
    </>
  );
};

type RowProps = {
  invoice: InvoiceInfoDto;
  onMenuClick: (menuParams: MenuParams<InvoiceInfoDto>) => void;
  readOnly?: boolean;
  withSalaryEntries: boolean;
};

const InvoiceListRow = ({ invoice, onMenuClick, withSalaryEntries, readOnly = false }: RowProps) => {
  const priceTotal = invoice.totals.discountPriceTotal;
  const reportTotals = invoice.report ? calculateInvoiceReportTotals(invoice.report.rows) : undefined;

  const { data } = useLastClosedInvoice(invoice.buyerCompany.id, invoice.id, !withSalaryEntries);

  const handleMenuClick = (e: MouseEvent<HTMLElement>) => {
    onMenuClick({ anchor: e.currentTarget, target: invoice });
  };

  return (
    <>
      <TableRow
        key={invoice.id}
      >
        <TableCell>
          <LinkToInvoice invoice={invoice}>{formatDate(invoice.startDate)}</LinkToInvoice>
        </TableCell>
        <TableCell>
          <LinkToInvoice invoice={invoice} />
          {invoice.isInternetAdvertising && (
            <Tooltip title="Реклама в интернете">
              <Chip label="ОРД" size="small" color="info" variant="outlined" sx={{ ml: 1 }} />
            </Tooltip>
          )}
        </TableCell>
        <TableCell>
          <LinkToCompany company={invoice.buyerCompany} />
        </TableCell>
        <TableCell>
          <LinkToLegalEntity legalEntity={invoice.seller} />
        </TableCell>
        <TableCell>
          <LinkToLegalEntity legalEntity={invoice.buyer} />
        </TableCell>
        <TableCell>
          <InvoiceStatusSpan status={invoice.status} size="small" />
        </TableCell>
        <TableCell>
          <ManagerSpan person={invoice.manager} />
        </TableCell>
        {withSalaryEntries && (
          <TableCell>
            {
              data ?
                <>
                  <LinkToInvoice invoice={data} /> <br />
                  {data?.managerPercent}% <br />
                  {`${formatPersonPruning(data.manager)}`}
                </>
                : '-'
            }
          </TableCell>
        )}
        <TableCell align='right'>
          <PriceSpan size="small" price={priceTotal} />
        </TableCell>
        {!withSalaryEntries && <TableCell align='right'>
          {reportTotals && (
            <PriceSpan
              size="small"
              price={reportTotals.totalProfitByPurchases}
              zeroAsDash
            />
          )}
        </TableCell>}
        <TableCell align='right'>
          {(reportTotals && reportTotals.sellerrRowsTotal && reportTotals.totalProfitByPurchases) ? (
            <PriceSpan
              size="small"
              price={calculateNetProfit(reportTotals, invoice.legalEntityTax)}
              zeroAsDash
            />
          ) : '-'}
        </TableCell>
        {withSalaryEntries && invoice.report ? (
          <InvoiceCloseForm invoiceId={invoice.id} rows={invoice.report.rows} totals={calculateInvoiceReportTotals(invoice.report.rows)} withTableView={true} />
        ) : (
          <>
            <TableCell>
              <WccButton invoiceId={invoice.id} invoiceType={invoice.type} initialWcc={invoice.wcc} />
            </TableCell>
            {!readOnly && (
              <TableCell align="right">
                <IconButton edge="end" onClick={handleMenuClick}>
                  <MoreVertIcon />
                </IconButton>
              </TableCell>
            )}
          </>
        )}
      </TableRow>
    </>
  );
};


type InvoiceListFooterProps = {
  rows: InvoiceInfoDto[]
  colSpan: number
  metaPriceTotal?: number,
  withSalaryEntries?: boolean,
  metaSalesByPurchasesTotal?: number,
  metaNetProfitTotal?: number,
}
const InvoiceListFooter = ({ rows, colSpan, metaSalesByPurchasesTotal, metaPriceTotal, withSalaryEntries, metaNetProfitTotal }: InvoiceListFooterProps) => {
  const priceTotal = metaPriceTotal ?? rows.reduce((acc, row) => acc + row.totals.discountPriceTotal, 0);
  const profitTotal = metaSalesByPurchasesTotal ?? rows.reduce((acc, row) => {
    if (!row.report) return acc;
    const totals = calculateInvoiceReportTotals(row.report.rows);
    return acc + totals.totalProfitByPurchases;
  }, 0);
  const netProfitTotal = metaNetProfitTotal ?? rows.reduce((acc, row) => {
    if (!row.report) return acc;
    const totals = calculateInvoiceReportTotals(row.report.rows);
    if (totals.sellerrRowsTotal === 0) return acc;
    return acc + calculateNetProfit(totals, row.legalEntityTax);
  }, 0);
  return (
    <TableRow>
      <TableCell colSpan={colSpan} align='right'>
        <Typography variant='subtitle1'>Итого:</Typography>
      </TableCell>
      <TableCell align='right'><PriceSpan price={priceTotal} size="small" /></TableCell>
      {!withSalaryEntries && <TableCell align='right'><PriceSpan price={profitTotal} size="small" zeroAsDash /></TableCell>}
      <TableCell align='right'><PriceSpan price={netProfitTotal} size="small" zeroAsDash /></TableCell>
      <TableCell />
    </TableRow>
  );
};

type InvoiceListHeaderProps = {
  rows: InvoiceInfoDto[];
  metaPriceTotal?: number;
  metaProfitTotal?: number;
  metaNetProfitTotal?: number;
  metaSalesPercentage?: number;
};
const InvoiceListHeader = ({ rows, metaPriceTotal, metaProfitTotal, metaNetProfitTotal = 0, metaSalesPercentage }: InvoiceListHeaderProps) => {
  const priceTotal = metaPriceTotal ?? rows.reduce((acc, row) => acc + row.totals.discountPriceTotal, 0);
  const profitTotal = metaProfitTotal ??
     rows.reduce((acc, row) => acc + (row.report ? calculateInvoiceReportTotals(row.report.rows).totalProfitByPurchases : 0), 0);
  
  const getColor = (value: number) => {
    if (value >= 30) return 'success';
    if (value <= 10) return 'error';
    return 'black';
  } ;

  return (
    <Card sx={{ mb: 2, padding: 1, paddingLeft: 2, paddingRight: 2 }}>
      <Stack direction={'row'} gap={8}>
        <Stack direction={'column'}>
          <Typography>Итого:</Typography>
        </Stack>

        <Stack direction={'row'} gap={4}>
          <Stack direction={'column'}>
            <Typography>Сумма</Typography>
            <Typography>
              <PriceSpan price={priceTotal} size="small" />
            </Typography>
          </Stack>
          <Stack direction={'column'}>
            <Typography>Прибыль по заявкам</Typography>
            <Typography>
              <PriceSpan price={profitTotal} size="small" zeroAsDash />
            </Typography>
          </Stack>
          <Stack direction={'column'}>
            <Typography>Чистая прибыль</Typography>
            <Typography>
              <PriceSpan price={metaNetProfitTotal} size="small" zeroAsDash />
            </Typography>
          </Stack>
          { metaSalesPercentage !== undefined && <Stack direction={'column'}>
            <Typography>Процент продаж</Typography>
            <Typography color={getColor(metaSalesPercentage)} fontWeight={500} fontSize={'1.1rem'}>{metaSalesPercentage.toFixed(2)}</Typography>
          </Stack>}
        </Stack>
      </Stack>
    </Card>
  );
};
